import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  generateKey as v2GenerateKey,
  encrypt as v2Encrypt,
  decrypt as v2Decrypt,
  parseEnvelope,
  serializeEnvelope,
  isV2Envelope,
} from '../encryption/DocumentEncryptionV2';
import { legacyAesDecrypt } from '../encryption/legacy/LegacyDecryption';
import { createLogger } from '../../utils/logger';

const logger = createLogger('VaultMigration');

// RNFS is optional (see documentStorageService.ts for the same pattern) — in
// Jest and simulators without native file support we fall through to a stub.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let RNFS: any;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  RNFS = require('react-native-fs');
} catch {
  RNFS = {
    CachesDirectoryPath: '/tmp/cache',
    exists: () => Promise.resolve(false),
    readFile: () => Promise.resolve(''),
    writeFile: () => Promise.resolve(),
    unlink: () => Promise.resolve(),
  };
}

export const MIGRATION_STATE_KEY = 'migration_v1_to_v2_state';
export const LOCAL_DOCUMENTS_KEY = 'local_documents';
export const VAULT_DOCUMENT_KEYS_KEY = 'vault_document_keys';

export type MigrationItemSource = 'local_document' | 'vault_document_key';

export interface MigrationItem {
  source: MigrationItemSource;
  id: string;
}

export interface MigrationPlan {
  itemsToMigrate: MigrationItem[];
  totalCount: number;
  alreadyMigrated: number;
}

export interface MigrationFailure {
  id: string;
  source: MigrationItemSource;
  error: string;
  rawData?: string;
  stage: 'read' | 'decrypt' | 'encrypt' | 'verify' | 'persist';
}

export interface MigrationSummary {
  total: number;
  migrated: number;
  failed: number;
  failures: MigrationFailure[];
  completedAt: string;
}

export type MigrationResult =
  | { status: 'ok'; itemId: string }
  | {
      status: 'failed' | 'failed_verify';
      itemId: string;
      stage: MigrationFailure['stage'];
      error: string;
      rawData?: string;
    };

export interface MigrationState {
  started: boolean;
  completed: boolean;
  startedAt: string | null;
  completedAt: string | null;
  lastProcessedIndex: number;
  total: number;
  failures: MigrationFailure[];
}

export interface MigrateAllOptions {
  onProgress?: (progress: {
    currentIndex: number;
    total: number;
    lastItem: MigrationItem | null;
  }) => void;
}

const DEFAULT_STATE: MigrationState = {
  started: false,
  completed: false,
  startedAt: null,
  completedAt: null,
  lastProcessedIndex: -1,
  total: 0,
  failures: [],
};

// Shape of the per-document entry in `local_documents` AsyncStorage. Matches
// the LocalDocumentStorage provider.
interface LegacyDocumentEntry {
  fileUrl: string;
  encryptedDocumentKey?: string;
  documentId?: string;
  fileName?: string;
  fileSize?: number;
  [key: string]: unknown;
}

/**
 * Scan known v1 storage locations and return the set of items that still
 * need migration. Safe to call repeatedly — it only reports items whose
 * ciphertext is not already in v2 format.
 */
export async function detectLegacyData(): Promise<MigrationPlan> {
  const items: MigrationItem[] = [];
  let alreadyMigrated = 0;

  // local_documents — per-file wrapped keys + on-disk encrypted files
  const docsRaw = await AsyncStorage.getItem(LOCAL_DOCUMENTS_KEY);
  if (docsRaw) {
    let docs: Record<string, LegacyDocumentEntry> = {};
    try {
      docs = JSON.parse(docsRaw);
    } catch {
      /* swallow — corrupt map; will be reported per-item if accessed */
    }
    for (const [id, entry] of Object.entries(docs)) {
      const wrapped = entry?.encryptedDocumentKey;
      const alreadyV2 = typeof wrapped === 'string' && isV2Envelope(wrapped);
      if (alreadyV2) {
        alreadyMigrated += 1;
        continue;
      }
      items.push({ source: 'local_document', id });
    }
  }

  // vault_document_keys — the offline-fallback copy of wrapped keys
  const keysRaw = await AsyncStorage.getItem(VAULT_DOCUMENT_KEYS_KEY);
  if (keysRaw) {
    let keys: Record<string, string> = {};
    try {
      keys = JSON.parse(keysRaw);
    } catch {
      /* ignore */
    }
    for (const [id, wrapped] of Object.entries(keys)) {
      if (typeof wrapped === 'string' && isV2Envelope(wrapped)) {
        alreadyMigrated += 1;
        continue;
      }
      items.push({ source: 'vault_document_key', id });
    }
  }

  return {
    itemsToMigrate: items,
    totalCount: items.length + alreadyMigrated,
    alreadyMigrated,
  };
}

export async function getMigrationState(): Promise<MigrationState> {
  const raw = await AsyncStorage.getItem(MIGRATION_STATE_KEY);
  if (!raw) return { ...DEFAULT_STATE };
  try {
    const parsed = JSON.parse(raw) as Partial<MigrationState>;
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

async function setMigrationState(state: MigrationState): Promise<void> {
  await AsyncStorage.setItem(MIGRATION_STATE_KEY, JSON.stringify(state));
}

export async function resetMigrationState(): Promise<void> {
  await AsyncStorage.removeItem(MIGRATION_STATE_KEY);
}

/**
 * Migrate a single item: read legacy ciphertext, decrypt, re-encrypt with
 * v2, write new ciphertext, verify the round-trip, then delete the legacy
 * artifact. The order is enforced: the old blob is never deleted until the
 * new blob has been written AND decrypted back to the original plaintext.
 */
export async function migrateItem(
  item: MigrationItem,
  legacyMasterKey: string,
  newMasterKey: string,
): Promise<MigrationResult> {
  try {
    if (item.source === 'local_document') {
      return await migrateLocalDocument(item.id, legacyMasterKey, newMasterKey);
    }
    return await migrateVaultDocumentKey(item.id, legacyMasterKey, newMasterKey);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      status: 'failed',
      itemId: item.id,
      stage: 'read',
      error: message,
    };
  }
}

async function migrateLocalDocument(
  id: string,
  legacyMasterKey: string,
  newMasterKey: string,
): Promise<MigrationResult> {
  const docsRaw = await AsyncStorage.getItem(LOCAL_DOCUMENTS_KEY);
  if (!docsRaw) {
    return { status: 'failed', itemId: id, stage: 'read', error: 'local_documents not found' };
  }
  const docs: Record<string, LegacyDocumentEntry> = JSON.parse(docsRaw);
  const entry = docs[id];
  if (!entry) {
    return { status: 'failed', itemId: id, stage: 'read', error: 'document not found' };
  }

  const wrappedV1 = entry.encryptedDocumentKey;
  const fileUrl = entry.fileUrl;
  if (!wrappedV1 || !fileUrl) {
    return {
      status: 'failed',
      itemId: id,
      stage: 'read',
      error: 'missing encryptedDocumentKey or fileUrl',
    };
  }

  // 1. Unwrap the legacy document key
  let legacyDocumentKey: string;
  try {
    legacyDocumentKey = await legacyAesDecrypt(wrappedV1, legacyMasterKey);
  } catch (err) {
    return {
      status: 'failed',
      itemId: id,
      stage: 'decrypt',
      error: err instanceof Error ? err.message : String(err),
      rawData: wrappedV1,
    };
  }

  // 2. Read the encrypted file body and decrypt it
  const exists = await RNFS.exists(fileUrl);
  if (!exists) {
    return {
      status: 'failed',
      itemId: id,
      stage: 'read',
      error: `encrypted file missing at ${fileUrl}`,
    };
  }

  let legacyFileCiphertext: string;
  let legacyPlaintextBase64: string;
  try {
    legacyFileCiphertext = await RNFS.readFile(fileUrl, 'utf8');
    legacyPlaintextBase64 = await legacyAesDecrypt(
      legacyFileCiphertext,
      legacyDocumentKey,
    );
  } catch (err) {
    return {
      status: 'failed',
      itemId: id,
      stage: 'decrypt',
      error: err instanceof Error ? err.message : String(err),
      rawData: fileUrl,
    };
  }

  // 3. Re-encrypt under a fresh v2 document key
  const newDocumentKey = await v2GenerateKey();
  const newFileEnvelope = await v2Encrypt(legacyPlaintextBase64, newDocumentKey);
  const newWrappedKeyEnvelope = await v2Encrypt(newDocumentKey, newMasterKey);
  const newFilePath = `${RNFS.CachesDirectoryPath}/v2_${Date.now()}_${id}`;

  // 4. Write new ciphertext to a NEW location (do not overwrite the old one yet)
  try {
    await RNFS.writeFile(newFilePath, serializeEnvelope(newFileEnvelope), 'utf8');
  } catch (err) {
    return {
      status: 'failed',
      itemId: id,
      stage: 'encrypt',
      error: err instanceof Error ? err.message : String(err),
    };
  }

  // 5. Verify the round-trip BEFORE deleting the old file
  try {
    const written = await RNFS.readFile(newFilePath, 'utf8');
    const envelope = parseEnvelope(written);
    const decrypted = await v2Decrypt(envelope, newDocumentKey);
    if (decrypted !== legacyPlaintextBase64) {
      // Something went wrong — clean up the new file, keep the old one
      await safeUnlink(newFilePath);
      return {
        status: 'failed_verify',
        itemId: id,
        stage: 'verify',
        error: 'round-trip plaintext mismatch',
      };
    }
  } catch (err) {
    await safeUnlink(newFilePath);
    return {
      status: 'failed_verify',
      itemId: id,
      stage: 'verify',
      error: err instanceof Error ? err.message : String(err),
    };
  }

  // 6. Commit: update the metadata map, then delete the old file
  try {
    const fresh: Record<string, LegacyDocumentEntry> = JSON.parse(
      (await AsyncStorage.getItem(LOCAL_DOCUMENTS_KEY)) ?? '{}',
    );
    fresh[id] = {
      ...entry,
      fileUrl: newFilePath,
      encryptedDocumentKey: serializeEnvelope(newWrappedKeyEnvelope),
    };
    await AsyncStorage.setItem(LOCAL_DOCUMENTS_KEY, JSON.stringify(fresh));
  } catch (err) {
    await safeUnlink(newFilePath);
    return {
      status: 'failed',
      itemId: id,
      stage: 'persist',
      error: err instanceof Error ? err.message : String(err),
    };
  }

  await safeUnlink(fileUrl);
  return { status: 'ok', itemId: id };
}

async function migrateVaultDocumentKey(
  id: string,
  legacyMasterKey: string,
  newMasterKey: string,
): Promise<MigrationResult> {
  const raw = await AsyncStorage.getItem(VAULT_DOCUMENT_KEYS_KEY);
  if (!raw) {
    return { status: 'failed', itemId: id, stage: 'read', error: 'vault_document_keys not found' };
  }
  const map: Record<string, string> = JSON.parse(raw);
  const wrappedV1 = map[id];
  if (!wrappedV1) {
    return { status: 'failed', itemId: id, stage: 'read', error: 'key not found' };
  }

  let legacyDocumentKey: string;
  try {
    legacyDocumentKey = await legacyAesDecrypt(wrappedV1, legacyMasterKey);
  } catch (err) {
    return {
      status: 'failed',
      itemId: id,
      stage: 'decrypt',
      error: err instanceof Error ? err.message : String(err),
      rawData: wrappedV1,
    };
  }

  const newWrapped = await v2Encrypt(legacyDocumentKey, newMasterKey);
  const newWrappedSerialized = serializeEnvelope(newWrapped);

  // Verify the round-trip before overwriting
  try {
    const env = parseEnvelope(newWrappedSerialized);
    const decrypted = await v2Decrypt(env, newMasterKey);
    if (decrypted !== legacyDocumentKey) {
      return {
        status: 'failed_verify',
        itemId: id,
        stage: 'verify',
        error: 'round-trip key mismatch',
      };
    }
  } catch (err) {
    return {
      status: 'failed_verify',
      itemId: id,
      stage: 'verify',
      error: err instanceof Error ? err.message : String(err),
    };
  }

  try {
    const fresh: Record<string, string> = JSON.parse(
      (await AsyncStorage.getItem(VAULT_DOCUMENT_KEYS_KEY)) ?? '{}',
    );
    fresh[id] = newWrappedSerialized;
    await AsyncStorage.setItem(VAULT_DOCUMENT_KEYS_KEY, JSON.stringify(fresh));
  } catch (err) {
    return {
      status: 'failed',
      itemId: id,
      stage: 'persist',
      error: err instanceof Error ? err.message : String(err),
    };
  }

  return { status: 'ok', itemId: id };
}

async function safeUnlink(path: string): Promise<void> {
  try {
    const exists = await RNFS.exists(path);
    if (exists) await RNFS.unlink(path);
  } catch (err) {
    logger.debug('safeUnlink failed (non-fatal)');
  }
}

/**
 * Migrate every v1-format item. Resumable-by-design: detectLegacyData() is
 * idempotent and already skips items whose wrapped key has been rewritten
 * in v2 format, so re-running after a crash picks up the remaining work
 * naturally. State is persisted after every item so failures are preserved
 * across launches and reflected in the final summary.
 */
export async function migrateAll(
  legacyMasterKey: string,
  newMasterKey: string,
  options: MigrateAllOptions = {},
): Promise<MigrationSummary> {
  const plan = await detectLegacyData();
  const prior = await getMigrationState();
  const resuming = prior.started && !prior.completed;

  const state: MigrationState = {
    started: true,
    completed: false,
    startedAt: resuming && prior.startedAt ? prior.startedAt : new Date().toISOString(),
    completedAt: null,
    lastProcessedIndex: -1,
    total: plan.itemsToMigrate.length + prior.failures.length,
    // Carry failures from prior runs forward; they're the things we already
    // tried and couldn't migrate.
    failures: resuming ? [...prior.failures] : [],
  };
  await setMigrationState(state);

  for (let i = 0; i < plan.itemsToMigrate.length; i++) {
    const item = plan.itemsToMigrate[i];

    // Skip items that already failed in a prior run to avoid retry loops.
    if (state.failures.some(f => f.id === item.id && f.source === item.source)) {
      state.lastProcessedIndex = i;
      await setMigrationState(state);
      options.onProgress?.({
        currentIndex: i + 1,
        total: plan.itemsToMigrate.length,
        lastItem: item,
      });
      continue;
    }

    const result = await migrateItem(item, legacyMasterKey, newMasterKey);

    if (result.status !== 'ok') {
      state.failures.push({
        id: result.itemId,
        source: item.source,
        error: result.error,
        rawData: result.rawData,
        stage: result.stage,
      });
    }

    state.lastProcessedIndex = i;
    // Persist after every item so a crash loses at most one step.
    await setMigrationState(state);

    options.onProgress?.({
      currentIndex: i + 1,
      total: plan.itemsToMigrate.length,
      lastItem: item,
    });
  }

  state.completed = true;
  state.completedAt = new Date().toISOString();
  await setMigrationState(state);

  const migrated = state.total - state.failures.length;
  return {
    total: state.total,
    migrated,
    failed: state.failures.length,
    failures: state.failures,
    completedAt: state.completedAt,
  };
}

/**
 * Serialize the failed items to JSON for the user to save out of the app
 * (via share sheet). Raw legacy ciphertext is included so the user retains
 * the only copy — they can choose to hand it to support for recovery.
 */
export function exportFailedItems(failures: MigrationFailure[]): string {
  const payload = {
    generatedAt: new Date().toISOString(),
    failures: failures.map(f => ({
      id: f.id,
      source: f.source,
      stage: f.stage,
      error: f.error,
      rawDataBase64Preview:
        typeof f.rawData === 'string'
          ? f.rawData.slice(0, 2048)
          : null,
    })),
  };
  return JSON.stringify(payload, null, 2);
}
